import { VStack, Text, Box, Image, Icon } from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@/store/store';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { IUser } from '@/services/auth/types';
import Logo from '@/assets/svg/logo.svg';
import DashboardIcon from '@/assets/svg/sidebar/dashboard.svg?react';
import LearningResourceIcon from '@/assets/svg/sidebar/learning-resource.svg?react';
import AddProductIcon from '@/assets/svg/sidebar/add-product.svg?react';
import ManageProductIcon from '@/assets/svg/sidebar/manage-product.svg?react';
import AllOrderIcon from '@/assets/svg/sidebar/all-orders.svg?react';
import ReviewIcon from '@/assets/svg/sidebar/reviews.svg?react';
import VoucherIcon from '@/assets/svg/sidebar/voucher-management.svg?react';
import UserIcon from '@/assets/svg/sidebar/user-management.svg?react';
import { isAuthenticated } from '@/routes';
import ReturnOrderIcon from '@/assets/svg/sidebar/return-orders.svg?react';
// import StylesAndAddOnsIcon from '@/assets/svg/sidebar/stylesAndAddons.svg?react';
// import ActiveStylesAndAddOnsIcon from '@/assets/svg/sidebar/stylesAndAddons-active.svg?react';
import PaymentsIcon from '@/assets/svg/sidebar/payments.svg?react';
import ActivePaymentsIcon from '@/assets/svg/sidebar/payments-active.svg?react';
import OptimizerIcon from '@/assets/svg/sidebar/optimizer.svg?react';
import ActiveOptimizerIcon from '@/assets/svg/sidebar/optimizer-active.svg?react';
// Role-based access control logic
interface SidebarChildItem {
  name: string;
  route: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  activeIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  permission: string | null;
}
const hasAccess = (user: IUser | null, path: string): boolean => {
  if (!user) return false;

  if (user.role === 'vendor') {
    return true;
  }

  const isAdmin = user.vendor_role_permissions?.some((role) =>
    role.permissions.some((perm) => perm.role_name === 'Admin'),
  );
  if (isAdmin) {
    return true;
  }

  const userRoles =
    user.vendor_role_permissions?.flatMap((role) =>
      role.permissions.map((perm) => perm.role_name),
    ) || [];

  const roleAccess: { [key: string]: string[] } = {
    'Review Management': [NAVIGATION_ROUTES.REVIEWS],
    'Coupon Management': [NAVIGATION_ROUTES.VOUCHER_MANAGEMENT],
    'Dashboard Management': [NAVIGATION_ROUTES.DASHBOARD],
    'Order Management': [NAVIGATION_ROUTES.ALL_ORDERS, NAVIGATION_ROUTES.RETURN_ORDERS],
    'Product Management': [NAVIGATION_ROUTES.ADD_PRODUCT, NAVIGATION_ROUTES.MANAGE_PRODUCT],
  };

  return userRoles.some((role) => roleAccess[role]?.includes(path));
};

// Sidebar items with sections and permissions
const sidebarItems: { title: string; children: SidebarChildItem[] }[] = [
  {
    title: 'Main',
    children: [
      {
        name: 'Dashboard',
        route: NAVIGATION_ROUTES.HOME,
        icon: DashboardIcon,
        permission: 'Dashboard Management',
      },
      {
        name: 'Learning Resource',
        route: NAVIGATION_ROUTES.LEARNING_RESOURCES,
        icon: LearningResourceIcon,
        permission: null,
      },
      {
        name: 'Image Optimizer',
        route: NAVIGATION_ROUTES.IMAGECROPPER,
        icon: OptimizerIcon,
        activeIcon: ActiveOptimizerIcon,
        permission: null,
      },
    ],
  },
  {
    title: 'Product Management',
    children: [
      {
        name: 'Add Product',
        route: NAVIGATION_ROUTES.ADD_PRODUCT,
        icon: AddProductIcon,
        permission: 'Product Management',
      },
      {
        name: 'Manage Product',
        route: NAVIGATION_ROUTES.MANAGE_PRODUCT,
        icon: ManageProductIcon,
        permission: 'Product Management',
      },
      // {
      //   name: 'Manage Addons',
      //   route: NAVIGATION_ROUTES.MANAGE_ADDONS,
      //   icon: StylesAndAddOnsIcon,
      //   activeIcon: ActiveStylesAndAddOnsIcon,
      //   permission: 'Product Management',
      // },
    ],
  },
  {
    title: 'Order Management',
    children: [
      {
        name: 'All Orders',
        route: NAVIGATION_ROUTES.ALL_ORDERS,
        icon: AllOrderIcon,
        permission: 'Order Management',
      },
      {
        name: 'Return Orders',
        route: NAVIGATION_ROUTES.RETURN_ORDERS,
        icon: ReturnOrderIcon,
        permission: 'Order Management',
      },
      {
        name: 'Reviews',
        route: NAVIGATION_ROUTES.REVIEWS,
        icon: ReviewIcon,
        permission: 'Review Management',
      },
      {
        name: 'Voucher Management',
        route: NAVIGATION_ROUTES.VOUCHER_MANAGEMENT,
        icon: VoucherIcon,
        permission: 'Coupon Management',
      },
      {
        name: 'Payment Details',
        route: NAVIGATION_ROUTES.PAYMENT_DETAILS,
        icon: PaymentsIcon,
        activeIcon: ActivePaymentsIcon,
        permission: 'Order Management',
      },
    ],
  },
  {
    title: 'User Management',
    children: [
      {
        name: 'User Management',
        route: NAVIGATION_ROUTES.USER_MANAGEMENT,
        icon: UserIcon,
        permission: 'Admin',
      },
    ],
  },
];

interface SideBarProps {
  closeDrawer?: () => void; // Optional prop to close the drawer
}

const SideBar = ({ closeDrawer }: SideBarProps) => {
  const { user } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Filter sidebar items based on user role
  const filteredSidebarItems = sidebarItems
    .map((section) => ({
      title: section.title,
      children: section.children.filter((item) => hasAccess(user, item.route)),
    }))
    .filter((section) => section.children.length > 0);

  const handleItemClick = (route: string) => {
    navigate(route);
    if (closeDrawer) {
      closeDrawer(); // Close the drawer if the prop is provided (mobile view)
    }
  };

  return (
    <VStack align="start" color="white" spacing={4} p={4} fontFamily="primary" w="250px" h="100vh">
      <Box w="full" px={4}>
        <Box
          as={Link}
          to={isAuthenticated() ? NAVIGATION_ROUTES.HOME : NAVIGATION_ROUTES.SIGNIN}
          display="inline-block"
          _focusVisible={{
            background: 'brand.primary.50',
            color: 'brand.primary.DEFAULT',
            outline: '2px solid',
            outlineColor: 'brand.primary.DEFAULT',
            outlineOffset: '2px',
          }}
          _active={{
            background: 'brand.primary.50',
            color: 'brand.primary.DEFAULT',
          }}
          onClick={() => closeDrawer && closeDrawer()}
        >
          <Image
            src={Logo}
            alt="Logo"
            width={{ base: '115px', lg: '170px' }}
            height="auto"
            objectFit="cover"
          />
        </Box>
        <Box
          w="full"
          h="1px"
          mt={4}
          bg="radial-gradient(50% 50% at 50% 50%, #0C3747 0%, rgba(12, 55, 71, 0) 100%)"
        />
      </Box>

      {filteredSidebarItems.map((sidebarItem, index) => (
        <VStack key={index} align="start" w="full" spacing={1}>
          <Text fontWeight="500" fontSize="sm" color="text.disable" pl={5}>
            {sidebarItem.title.toUpperCase()}
          </Text>
          {sidebarItem.children.map((child, idx) => {
            const isActive = location.pathname === child.route;
            return (
              <Box
                key={idx}
                as="button"
                onClick={() => handleItemClick(child.route)}
                px={4}
                py={3}
                w="full"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                background={isActive ? 'brand.primary.50' : 'background.white'}
                borderRadius="md"
                minH="32px"
                _hover={{
                  background: 'brand.primary.50',
                  textDecoration: 'none',
                }}
                _focusVisible={{
                  background: 'brand.primary.50',
                  color: 'brand.primary.DEFAULT',
                  outline: '2px solid',
                  outlineColor: 'brand.primary.DEFAULT',
                  outlineOffset: '2px',
                }}
                _active={{
                  background: 'brand.primary.50',
                  color: 'brand.primary.DEFAULT',
                }}
              >
                {child.icon && (
                  <Icon
                    as={isActive && child.activeIcon ? child.activeIcon : child.icon}
                    color={isActive ? 'brand.primary.DEFAULT' : 'text.secondary'}
                    boxSize={5}
                  />
                )}
                <Text
                  ml={2}
                  color={isActive ? 'brand.primary.DEFAULT' : 'text.secondary'}
                  fontSize="sm"
                >
                  {child.name}
                </Text>
              </Box>
            );
          })}
          {index < filteredSidebarItems.length - 1 && (
            <Box
              w="full"
              h="1px"
              mt={2}
              bg="radial-gradient(50% 50% at 50% 50%, #0C3747 0%, rgba(12, 55, 71, 0) 100%)"
            />
          )}
        </VStack>
      ))}
    </VStack>
  );
};

export default SideBar;
